import React, { useState, useEffect } from 'react';
import { API_BASE } from '../../config';
import { CitationsBlock } from '../Citations';

import { useEscapeToClose } from '../../hooks/useEscapeToClose';
function HealthSummaryModal({ onClose, fetchWithAuth, user }) {
  useEscapeToClose(onClose);   // Escape dismisses this modal
  const [summaryData, setSummaryData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [emailing, setEmailing] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  useEffect(() => {
    loadHealthSummary();
  }, []);

  const loadHealthSummary = async () => {
    try {
      const response = await fetchWithAuth(`${API_BASE}/api/dashboard/health-summary`);
      if (response.ok) {
        const data = await response.json();
        setSummaryData(data);
      }
    } catch (error) {
      console.error('Failed to load health summary:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEmailPDF = async () => {
    setEmailing(true);
    try {
      const response = await fetchWithAuth(`${API_BASE}/api/dashboard/email-health-summary`, {
        method: 'POST'
      });

      if (response.ok) {
        setEmailSent(true);
        setTimeout(() => setEmailSent(false), 3000);
      } else {
        alert('Failed to send email. Please try again.');
      }
    } catch (error) {
      console.error('Failed to email health summary:', error);
      alert('Failed to send email. Please try again.');
    } finally {
      setEmailing(false);
    }
  };

  // Section renderer
  const renderSection = (title, data, renderFn) => {
    if (!data || (Array.isArray(data) && data.length === 0)) {
      return null;
    }
    return (
      <div className="summary-section">
        <h4>{title}</h4>
        {renderFn(data)}
      </div>
    );
  };

  return (
    <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal-content modal-large hsm-modal" >
        <div className="modal-header">
          <h3>Complete Health Summary</h3>
          <div className="hsm-headerActions">
            {emailSent && (
              <span className="hsm-emailSent">Sent!</span>
            )}
            <button
              className="hsm-emailBtn"
              onClick={handleEmailPDF}
              disabled={emailing || loading}
            >
              {emailing ? 'Sending...' : 'Email PDF'}
            </button>
            <button className="hsm-closeBtn" onClick={onClose}>×</button>
          </div>
        </div>
        
        <div className="modal-body hsm-body" >
          {loading ? (
            <div className="empty-state-card">
              <p>Loading health summary...</p>
            </div>
          ) : !summaryData ? (
            <div className="empty-state-card">
              <p>Failed to load health summary. Please try again.</p>
            </div>
          ) : (
            <div className="health-summary-content">
              {/* User Info */}
              <div className="summary-header" >
                <h2 className="dash-userName">
                  {summaryData.user?.name || 'Health Summary'}
                </h2>
                <p className="dash-generatedDate">
                  Generated: {new Date(summaryData.generated_at).toLocaleString()}
                </p>
              </div>

              {/* Healist Score */}
              {summaryData.healist_score && (
                <div className="dash-scoreCard">
                  <div className="dash-scoreLabel">Healist Score</div>
                  <div className="dash-scoreValue">
                    {summaryData.healist_score.value}
                  </div>
                  <div className="dash-scoreChange">
                    {summaryData.healist_score.change}
                  </div>
                  {/* Apple 1.4.1 (G12, 2026-10-17): the score's standards, one tap away. */}
                  <CitationsBlock featureName="Healist Score" itemTitle="your Healist Score"
                                  testId="health-summary-score-citations" />
                </div>
              )}

              {/* Body Characteristics */}
              {renderSection('Body Characteristics', summaryData.body_characteristics, (data) => (
                <div className="data-grid dash-dataGrid3" >
                  {data.height && <div><strong>Height:</strong> {data.height}</div>}
                  {data.weight && <div><strong>Weight:</strong> {data.weight}</div>}
                  {data.age && <div><strong>Age:</strong> {data.age}</div>}
                  {data.gender && <div><strong>Gender:</strong> {data.gender}</div>}
                  {data.activity_level && <div><strong>Activity:</strong> {data.activity_level}</div>}
                  {data.bmr && <div><strong>BMR:</strong> {data.bmr} cal</div>}
                </div>
              ))}

              {/* Health Objectives */}
              {renderSection('Health Objectives', summaryData.health_objectives, (data) => (
                <ul className="summary-list">
                  {data.map((obj, i) => (
                    <li key={i}>
                      <strong>{obj.title}</strong>
                      {obj.description && <span> - {obj.description}</span>}
                      {obj.status && <span className={`status-badge ${obj.status}`}> ({obj.status})</span>}
                    </li>
                  ))}
                </ul>
              ))}

              {/* Prescriptions & Supplements */}
              {renderSection('Prescriptions & Supplements', summaryData.prescriptions_supplements, (data) => (
                <ul className="summary-list">
                  {data.map((p, i) => (
                    <li key={i}>
                      <strong>{p.name}</strong>
                      {p.dosage && <span> - {p.dosage}</span>}
                      {p.frequency && <span> ({p.frequency})</span>}
                    </li>
                  ))}
                </ul>
              ))}

              {/* Food Preferences */}
              {renderSection('Food Preferences', summaryData.food_preferences, (data) => (
                <div className="flex flex-wrap gap-8">
                  {data.map((p, i) => (
                    <span key={i} className="dash-prefTag" style={{ background: p.type === 'allergy' ? '#FEE2E2' : p.type === 'dislike' ? '#FEF3C7' : '#DBEAFE' }}>
                      {p.type === 'allergy' && ''}
                      {p.item} ({p.type})
                    </span>
                  ))}
                </div>
              ))}

              {/* Active Meal Plan */}
              {renderSection('Active Meal Plan', summaryData.active_meal_plan, (data) => (
                <div className="data-grid dash-dataGrid2" >
                  <div><strong>Plan:</strong> {data.name}</div>
                  <div><strong>Type:</strong> {data.type}</div>
                  <div><strong>Daily Calories:</strong> {data.calories} cal</div>
                  {data.protein && <div><strong>Protein:</strong> {data.protein}g</div>}
                  {data.carbs && <div><strong>Carbs:</strong> {data.carbs}g</div>}
                  {data.fat && <div><strong>Fat:</strong> {data.fat}g</div>}
                </div>
              ))}

              {/* Connected Devices */}
              {renderSection('Connected Devices', summaryData.connected_devices, (data) => (
                <div className="flex flex-wrap gap-8">
                  {data.map((d, i) => (
                    <span key={i} className="dash-deviceTag">
                      {d.name || d.type}
                      {d.last_sync && <span className="text-gray-500"> (synced {new Date(d.last_sync).toLocaleDateString()})</span>}
                    </span>
                  ))}
                </div>
              ))}

              {/* Healthcare Providers */}
              {renderSection('Healthcare Providers', summaryData.healthcare_providers, (data) => (
                <ul className="summary-list">
                  {data.map((p, i) => (
                    <li key={i}>
                      <strong>{p.name}</strong>
                      {p.specialty && <span> - {p.specialty}</span>}
                    </li>
                  ))}
                </ul>
              ))}

              {/* Medical Records (limited) */}
              {renderSection('Recent Medical Records', summaryData.medical_records, (data) => (
                <ul className="summary-list">
                  {data.slice(0, 5).map((r, i) => (
                    <li key={i}>
                      <strong>{r.type}</strong>
                      {r.date && <span> - {new Date(r.date).toLocaleDateString()}</span>}
                      {r.provider && <span> ({r.provider})</span>}
                    </li>
                  ))}
                </ul>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


export default HealthSummaryModal;
